import { Link } from "react-router-dom";
import { AlertTriangle, Info } from "lucide-react";
import { APP_ROUTES, STORAGE_KEYS } from "../../../constants";
import { useLoginForm } from "../../../hooks/auth/useLoginForm";

import { EmailInput, PasswordInput, InstanceNameInput } from "../inputs";

import SocialAuthButtons from "../SocialAuthButtons";

const LoginForm = () => {
  const {
    register,
    handleSubmit,
    onSubmit,
    togglePasswordVisibility,
    handleEmailKeyDown,
    focusedField,
    setFocusedField,
    showPassword,
    validationErrors,
    emailValue,
    formInteracted,
    isSubmittingForm,
    loading,
    formHasValues,
    authError,
    instanceNameValue,
  } = useLoginForm();

  const rememberedEmail = localStorage.getItem(STORAGE_KEYS.REMEMBER_EMAIL);
  const isEmailRestored =
    emailValue && rememberedEmail === emailValue && !authError;

  return (
    <div>
      <div className="text-center mb-8">
        <h2 className="text-3xl font-extrabold text-gray-900">
          Sign in to your account
        </h2>
      </div>

      {authError && (
        <div className="rounded-md bg-red-50 p-4 border-l-4 border-red-500 mb-4 animate-fadeIn">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Authentication failed
              </h3>
              <p className="mt-1 text-sm text-red-700">{authError}</p>
            </div>
          </div>
        </div>
      )}

      {isEmailRestored && (
        <div className="rounded-md bg-blue-50 p-4 border-l-4 border-blue-500 mb-4 animate-fadeIn">
          <div className="flex">
            <Info className="h-5 w-5 text-blue-500 flex-shrink-0" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                We've restored your email from your last session.
              </p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <EmailInput
          register={register}
          validationError={validationErrors.email}
          emailValue={emailValue}
          focusedField={focusedField}
          setFocusedField={setFocusedField}
          handleKeyDown={handleEmailKeyDown}
        />

        <PasswordInput
          register={register}
          validationError={validationErrors.password}
          showPassword={showPassword}
          togglePasswordVisibility={togglePasswordVisibility}
          focusedField={focusedField}
          setFocusedField={setFocusedField}
        />

        <InstanceNameInput
          register={register}
          validationError={validationErrors.instanceName}
          instanceNameValue={instanceNameValue}
          focusedField={focusedField}
          setFocusedField={setFocusedField}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              {...register("rememberMe")}
              id="rememberMe"
              name="rememberMe"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded cursor-pointer"
            />
            <label
              htmlFor="rememberMe"
              className="ml-2 block text-sm text-gray-700 cursor-pointer"
            >
              Remember my email
            </label>
          </div>
          <Link
            to={APP_ROUTES.FORGOT_PASSWORD}
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500 cursor-pointer"
          >
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={
            loading ||
            (!formInteracted && !formHasValues) ||
            Object.keys(validationErrors).length > 0
          }
          className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${
            (!formInteracted && !formHasValues) ||
            Object.keys(validationErrors).length > 0
              ? "bg-indigo-700 cursor-not-allowed opacity-50"
              : "bg-indigo-600 hover:bg-indigo-700 cursor-pointer"
          } ${loading ? "opacity-80" : ""}`}
        >
          {loading
            ? "Signing in..."
            : isSubmittingForm
            ? "Processing..."
            : "Sign in"}
        </button>

        <SocialAuthButtons />
      </form>
    </div>
  );
};

export default LoginForm;
